import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { toggleDialog } from '../../../../../redux/Slices/Global/globalSlice';
import { ALL_DIRECTIONS, ANIM_TYPES } from '../../../../../config/campaigns';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from '../../../../../components/ui/Button';
import { LazyMotion, domAnimation } from 'framer-motion';
import AnimatedMiniPlayer from '../../../../Campaign/Components/setup/component/settings/AnimatedMiniPlayer';
// ⬇️ use your layout-level mutation (single call)
import { useUpdateScreenAnimationSettingsMutation } from '../../../../../queries';
import { useParams } from 'react-router-dom';

const toOptions = (arr) => arr.map((v) => ({ label: v, value: v }));
const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

// Make a valid animation object
const sanitizeAnim = (raw) => {
    const type = raw?.type ?? 'FADE';
    const enabled = !!raw?.enabled;
    const duration = Number.isFinite(raw?.duration) ? clamp(raw.duration, 0.2, 2.0) : 0.5;

    const allowed = (() => {
        if (type === 'FADE' || type === 'DISSOLVE') return [];
        if (type === 'FLIP') return ALL_DIRECTIONS.FLIP;
        if (type === 'SLIDE') return ALL_DIRECTIONS.SLIDE;
        if (type === 'ZOOM') return ALL_DIRECTIONS.ZOOM;
        if (type === 'SCROLL') return ALL_DIRECTIONS.SCROLL;
        if (type === 'ROLL') return ALL_DIRECTIONS.ROLL;
        return [];
    })();

    let direction =
        type === 'FADE' || type === 'DISSOLVE'
            ? 'CENTER'
            : raw?.direction && allowed.includes(raw.direction)
                ? raw.direction
                : type === 'FLIP'
                    ? 'HORIZONTAL'
                    : 'LEFT';

    return { enabled, type, duration, direction };
};

// Campaign id resolver (robust to different shapes)
const getCampaignIdFromScreen = (sc) =>
    sc?.id ?? sc?.pivot?.campaign_id ?? null;

// Read a screen's media_animation (MAIN only)
const getScreenAnimFromStore = (screen) => {
    const mediaAnim = screen?.settings?.live?.media_animation ?? null;
    return sanitizeAnim(mediaAnim || {});
};

function ScreenAnimationSettings() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { openDialog } = useSelector((s) => s.global) ?? {};

    const { screens_setting, selectedLayout, screenDetails } = useSelector((s) => s.layout) ?? {};
    const { id } = useParams();

    // single-call layout mutation
    const layoutAnimMutation = useUpdateScreenAnimationSettingsMutation();

    // resolve layout id from store or URL
    const layoutId = selectedLayout?.id ?? id;

    // Seed "common" from current campaign (fallback)
    const currentCommon = sanitizeAnim(screenDetails?.settings?.live?.media_animation);

    // Heuristic: are all screens enabled?


    // Apply to all SCREENS toggle
    const [allScreens, setAllScreens] = useState(false);

    // Common settings when ON
    const [common, setCommon] = useState(currentCommon);

    // Per-screen settings when OFF (keyed by campaign_id)
    const [perScreen, setPerScreen] = useState({});

    // Local saving state (single call now)
    const [saving, setSaving] = useState(false);

    // Sync from store
    useEffect(() => {
        const next = {};
        for (const sc of screens_setting) {
            const id = getCampaignIdFromScreen(sc);
            if (!id) continue;
            next[id] = getScreenAnimFromStore(sc);
        }
        setPerScreen(next);

        const seed =
            sanitizeAnim(screenDetails?.settings?.live?.media_animation) ||
            (screens_setting[0] ? getScreenAnimFromStore(screens_setting[0]) : currentCommon);
        setCommon(seed);

        // const allEn = screens_setting.length
        //     ? screens_setting.every((sc) => getScreenAnimFromStore(sc).enabled)
        //     : seed.enabled;
        // setAllScreens(allEn);
    }, [screenDetails, screens_setting]);

    // Direction options
    const directionOptionsFor = useCallback((type) => {
        if (type === 'FADE' || type === 'DISSOLVE') return [];
        if (type === 'FLIP') return toOptions(ALL_DIRECTIONS.FLIP);
        if (type === 'SLIDE') return toOptions(ALL_DIRECTIONS.SLIDE);
        if (type === 'ZOOM') return toOptions(ALL_DIRECTIONS.ZOOM);
        if (type === 'SCROLL') return toOptions(ALL_DIRECTIONS.SCROLL);
        if (type === 'ROLL') return toOptions(ALL_DIRECTIONS.ROLL);
        return [];
    }, []);

    // Common patcher
    const setCommonField = (patch) => {
        setCommon((prev) => {
            const next = sanitizeAnim({ ...prev, ...patch });
            if (patch.type) {
                const allowed = directionOptionsFor(next.type).map((o) => o.value);
                if (allowed.length === 0) next.direction = 'CENTER';
                else if (!allowed.includes(next.direction))
                    next.direction = next.type === 'FLIP' ? 'HORIZONTAL' : 'LEFT';
            }
            return next;
        });
    };

    // Per-screen patcher
    const setScreenField = (screenKey, patch) => {
        setPerScreen((prev) => {
            const current = prev[screenKey] ?? sanitizeAnim({});
            const next = sanitizeAnim({ ...current, ...patch });
            if (patch.type) {
                const allowed = directionOptionsFor(next.type).map((o) => o.value);
                if (allowed.length === 0) next.direction = 'CENTER';
                else if (!allowed.includes(next.direction))
                    next.direction = next.type === 'FLIP' ? 'HORIZONTAL' : 'LEFT';
            }
            return { ...prev, [screenKey]: next };
        });
    };

    // Build flat anim (MAIN-only)
    const flat = (anim) => ({
        enabled: anim.enabled,
        type: anim.type,
        duration: Number(clamp(anim.duration, 0.2, 2.0)),
        direction: anim.type === 'FADE' || anim.type === 'DISSOLVE' ? 'CENTER' : anim.direction,
    });

    // SINGLE CALL save
    const handleSaveAnimation = useCallback(async () => {
        if (!layoutId || !screens_setting?.length) return;
        setSaving(true);
        try {
            let payload;
            if (allScreens) {
                payload = { all_screens: true, ...flat(common) };
            } else {
                const screens = screens_setting
                    .map((sc) => {
                        const campaign_id = getCampaignIdFromScreen(sc);
                        if (!campaign_id) return null;
                        const state = perScreen[campaign_id] ?? getScreenAnimFromStore(sc);
                        return { campaign_id, ...flat(state) };
                    })
                    .filter(Boolean);
                payload = { all_screens: false, screens };
            }

            await layoutAnimMutation.mutateAsync({ id: layoutId, data: payload });
        } finally {
            setSaving(false);
        }
    }, [layoutId, screens_setting, allScreens, common, perScreen, layoutAnimMutation]);

    // Preview
    const previewAnim = allScreens
        ? common
        : (screens_setting[0] &&
            (perScreen[getCampaignIdFromScreen(screens_setting[0])] ??
                getScreenAnimFromStore(screens_setting[0]))) || common;

    const onHide = () => dispatch(toggleDialog('screenSettings'));

    // UI controls
    const renderAnimControls = (state, onChange) => {
        const dirOptions =
            state.type === 'FADE' || state.type === 'DISSOLVE' ? [] : directionOptionsFor(state.type);

        return (
            <>
                <div className="mb-3 flex w-full items-center justify-between">
                    <label className="text-sm">{t('layouts.setup_enable_media_transition')}</label>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-500">{state.enabled ? t('layouts.setup_on') : t('layouts.setup_off')}</span>
                        <InputSwitch checked={state.enabled} onChange={(e) => onChange({ enabled: !!e.value })} />
                    </div>
                </div>

                {state.enabled && (
                    <>
                        <div className="mb-3">
                            <Dropdown
                                value={state.type}
                                options={ANIM_TYPES}
                                onChange={(e) => onChange({ type: e.value })}
                                className="w-full"
                                placeholder={t('layouts.setup_select_animation_type')}
                                disabled={!state.enabled}
                            />
                        </div>

                        <div className="w-full flex items-center justify-between mb-2">
                            <label className="w-1/2">{t('layouts.setup_duration_sec')}</label>
                            <InputNumber
                                value={state.duration}
                                onValueChange={(e) => onChange({ duration: clamp(Number(e.value ?? 0.5), 0.2, 2.0) })}
                                mode="decimal"
                                minFractionDigits={1}
                                maxFractionDigits={1}
                                step={0.5}
                                min={0.5}
                                max={2.0}
                                showButtons
                                buttonLayout="stacked"
                                incrementButtonIcon="pi pi-chevron-up"
                                decrementButtonIcon="pi pi-chevron-down"
                                className="w-1/2"
                                disabled={!state.enabled}
                            />
                        </div>

                        {state.type !== 'FADE' && state.type !== 'DISSOLVE' && (
                            <div className="w-full flex items-center justify-between">
                                <label className="w-1/2">{t('layouts.setup_direction')}</label>
                                <Dropdown
                                    value={state.direction}
                                    options={dirOptions}
                                    onChange={(e) => onChange({ direction: e.value })}
                                    className="w-1/2 text-sm"
                                    placeholder={t('layouts.setup_select_direction')}
                                    disabled={!state.enabled}
                                />
                            </div>
                        )}
                    </>
                )}
            </>
        );
    };


    const renderFooter = () => {
        return (
            <div className="flex   w-full  justify-end">
                <Button
                    label={saving ? t('layouts.setup_saving') : t('layouts.setup_save_and_apply')}
                    className="py-1"
                    onClick={handleSaveAnimation}
                    disabled={saving || layoutAnimMutation.isLoading || !layoutId || !screens_setting?.length}
                />
            </div>
        )
    }

    return (
        <Dialog
            id='assignDeviceDialog'
            header={t('layouts.setup_screen_media_animation')}
            visible={openDialog?.screenSettings}
            onHide={onHide}
            footer={renderFooter}
            breakpoints={{ '960px': '95vw' }}
            style={{ width: '30vw', minHeight: '60vh', maxHeight: '100vh' }}
        >
            <div className="w-full mb-4 flex justify-center items-center">
                <div className="flex justify-center items-center flex-col gap-3 w-full mt-5" style={{ maxWidth: 360 }}>
                    {/* Preview */}
                    <LazyMotion features={domAnimation}>
                        <AnimatedMiniPlayer
                            enabled={previewAnim.enabled}
                            type={previewAnim.type}
                            direction={previewAnim.direction}
                            durationMs={previewAnim.duration * 1000}
                        />
                    </LazyMotion>

                    {/* Apply to all SCREENS */}
                    <div className="flex w-full items-center justify-between">
                        <label className="text-sm">{t('layouts.setup_apply_all_screens')}</label>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-500">{allScreens ? t('buttons.yes') : t('buttons.no')}</span>
                            <InputSwitch checked={allScreens} onChange={(e) => setAllScreens(!!e.value)} />
                        </div>
                    </div>

                    {/* COMMON (all screens) */}
                    {allScreens ? (
                        <div className="w-full">
                            <label className="text-sm text-start w-full">{t('layouts.setup_animation_all_screens_note')}</label>
                            <div className="px-2 py-3  ">{renderAnimControls(common, setCommonField)}</div>
                        </div>
                    ) : (
                        // PER-SCREEN
                        <div className="w-full h-[300px] overflow-y-auto scroll_div">
                            <Accordion multiple activeIndex={[0]}>
                                {(screens_setting?.length ? screens_setting : []).map((sc, idx) => {
                                    const campaignId = getCampaignIdFromScreen(sc);
                                    const key = campaignId ?? sc?.id ?? idx;
                                    const title = sc?.name || sc?.screen_name || sc?.title || `${t('layouts.setup_screen')} ${idx + 1}`;
                                    const state = perScreen[key] ?? getScreenAnimFromStore(sc) ?? sanitizeAnim({});

                                    return (
                                        <AccordionTab key={key} header={title} headerClassName="animation-title p-0">
                                            <div className="  ">
                                                {renderAnimControls(state, (patch) => setScreenField(key, patch))}
                                            </div>
                                        </AccordionTab>
                                    );
                                })}
                            </Accordion>
                        </div>
                    )}


                </div>
            </div>
        </Dialog>
    );
}

export default ScreenAnimationSettings;
